import React, { FunctionComponent, useState } from "react";

import styled, { css } from "styled-components";
import SV from "../../styles/variables";
import { Option } from "../../models/select";

import useOutsideClick from "../../hooks/useOutsideClick";

interface IProps {
  title: string;
  options: Option[];
  setState: React.Dispatch<React.SetStateAction<Option[]>>;
  limit?: number;
  dropdownTitle?: string;
  className?: string;
}

const Select: FunctionComponent<IProps> = ({
  title,
  options,
  setState,
  limit = 100,
  dropdownTitle,
  className
}) => {
  const [openState, setStateOpen] = useState(false);

  const ref = React.useRef(null);
  useOutsideClick(ref, e => {
    setStateOpen(false);
  });

  const GetSelectedCount = () => {
    return options.reduce((acc, cur) => {
      return cur.selected ? acc + 1 : acc;
    }, 0);
  };
  return (
    <SelectContainer ref={ref} className={className}>
      <SelectButton
        onClick={() => {
          setStateOpen(!openState);
        }}
      >
        <SelectTitle>{title} </SelectTitle>
        <SelectTick />
      </SelectButton>
      {openState && (
        <SelectItemsContainer>
          {dropdownTitle && (
            <SelectItemsTitle> {dropdownTitle} </SelectItemsTitle>
          )}
          {options.map(option => {
            return (
              <SelectItem
                selected={option.selected}
                onClick={() => {
                  setState(opts => {
                    const newOpts = [...opts];
                    const currentOption = newOpts.find(
                      opt => opt.name == option.name
                    );
                    if (currentOption) {
                      if (currentOption.selected) {
                        currentOption.selected = false;
                      } else if (GetSelectedCount() < limit) {
                        currentOption.selected = true;
                      }
                    }

                    return newOpts;
                  });
                }}
              >
                <Checkbox selected={option.selected} />
                {option.color && (
                  <ColorDot color={option.color} selected={option.selected} />
                )}
                <Name selected={option.selected}>{option.name}</Name>
              </SelectItem>
            );
          })}
        </SelectItemsContainer>
      )}
    </SelectContainer>
  );
};

const selectHeight = 32;
const selectWidth = 112;
const dropDownGap = 4;
const dropDownWidth = 168;

interface SelectProps {
  selected: boolean;
  onClick?: () => void;
}

const SelectContainer = styled.div`
  display: inline-block;
  position: relative;
`;

const SelectButton = styled.button`
  align-items: center;
  background-color: white;
  border-radius: 4px;
  border: solid 1px ${SV.colorP3};
  display: flex;
  height: ${selectHeight}px;
  line-height: 1;
  justify-content: space-between;
  padding: 8px;
  width: 112px;
`;
const SelectTitle = styled.span`
  color: ${SV.colorP2};
  font-size: 12px;
`;

const tickLength = 6;
const SelectTick = styled.span`
  border: 1px solid ${SV.colorP3};
  border-right: 1px;
  border-top: 1px;
  display: block;
  height: ${tickLength}px;
  transform: rotate(-45deg) translateY(${-tickLength / 2.7}px);
  width: ${tickLength}px;
`;

const SelectItemsContainer = styled.div`
  align-items: center;
  background-color: white;
  border-radius: 4px;
  border: solid 1px #cedfff;
  display: flex column;
  justify-content: center;
  left: ${selectWidth - dropDownWidth}px;
  line-height: 0;
  padding: 16px 0;
  position: absolute;
  text-align: left;
  top: ${selectHeight + dropDownGap}px;
  width: ${dropDownWidth}px;
  z-index: 10;
`;

const SelectItem = styled.div<SelectProps>`
  background-color: white;
  color: ${p => (p.selected ? SV.colorP3 : "#c7c7c7")};
  display: flex;
  font-size: 16px;
  justify-content: flex-start;
  align-items: center;
  align-self: center;
  font-weight: 300;
  line-height: 1;
  padding: 8px 24px;
` as React.FunctionComponent<SelectProps>;

const SelectItemsTitle = styled.div`
  display: inline-block;
  font-size: 12px;
  padding: 8px 24px;

  font-weight: 500;
  line-height: 1.33;
  color: #5d5d5d;
`;

const Check = css`
  content: "";
  border: 1px solid blue;
  border-top: 1px;
  border-right: 1px;
  display: block;
  height: 5px;
  left: 14px;
  top: 0px;
  transform: rotate(-45deg);
  transform: translate(4px, 2px) rotate(-45deg);
  width: 8px;
`;

const Checkbox = styled.span<SelectProps>`
  border: 1px ${p => (p.selected ? "#3a76eb" : "#c7c7c7")} solid;
  border-radius: 3px;
  display: inline-block;
  height: 16px;
  width: 16px;

  &::after {
    ${p => (p.selected ? Check : "")}
  }
` as React.FunctionComponent<SelectProps>;

interface ColorProps {
  color: string;
  selected: boolean;
}

const ColorDot = styled.span<ColorProps>`
  border: 1px ${p => p.color} solid;
  border-radius: 50%;
  background-color: ${p => (p.selected && p.color ? p.color : "white")}
  display: inline-block;
  height: 12px;
  margin-left: 8px;
  width: 12px;
` as React.FunctionComponent<ColorProps>;

const Name = styled.span<SelectProps>`
  display: block;
  color: ${p => (p.selected ? "black" : "#c7c7c7")};
  font-weight: 300;
  margin-left: 8px;
` as React.FunctionComponent<SelectProps>;

export default Select;
